import Box from '@mui/material/Box';
import ListSubheader from '@mui/material/ListSubheader';
import ArchivedChannelsButton from 'components/@home/@messages/ChannelsPanel/ArchivedChannelsButton';
import resolveMessagesChannelRedirect, {
  channelIdsEqual,
  firstVisibleChannelId,
} from 'components/@home/@messages/resolveMessagesChannelRedirect';
import isEqualWith from 'lodash/isEqualWith';
import { array, bool, func, number, object, string } from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { List } from 'react-window';
import channelsActions from 'store/app/entities/channels/action';
import messagesActions from 'store/app/entities/messages/action';
import authEmployeeSelector from 'store/selectors/authEmployee';
import currentChannelId from 'store/selectors/currentChannelId';
import Channel from './Channel';

const actions = {
  channels: channelsActions,
  messages: messagesActions,
};

const compareChannel = (o1, o2) =>
  isEqualWith(o1, o2, 'updatedAt') &&
  isEqualWith(o1, o2, 'lastMessage.createdAt') &&
  isEqualWith(o1, o2, 'messagesUnreadCount') &&
  isEqualWith(o1, o2, 'isActive') &&
  isEqualWith(o1, o2, 'avatar') &&
  isEqualWith(o1, o2, 'pinned');

const Row = ({
  ariaAttributes,
  index,
  style,
  visibleChannels,
  currentId,
  messageId = null,
  onSelect,
  onClickJoin = null,
}) => {
  const channel = visibleChannels[index];
  if (channel.separator) {
    return (
      <div {...ariaAttributes} style={style}>
        <ListSubheader>{channel.separator}</ListSubheader>
      </div>
    );
  }
  return (
    <div {...ariaAttributes} style={style}>
      <Channel
        channel={channel}
        selected={channelIdsEqual(channel._id, currentId) && channel.messageId === messageId}
        onClick={onSelect(channel._id, channel.messageFromSearch ? channel.lastMessage._id : null)}
        onClickJoin={onClickJoin && onClickJoin(channel._id)}
        isLast={index === visibleChannels.length - 1 || visibleChannels[index + 1].separator}
        fillRow
      />
    </div>
  );
};

Row.propTypes = {
  ariaAttributes: object.isRequired,
  index: number.isRequired,
  style: object.isRequired,
  visibleChannels: array.isRequired,
  currentId: object,
  messageId: object,
  onSelect: func.isRequired,
  onClickJoin: func,
};

const Channels = ({
  channels,
  messageId = null,
  onSelect,
  onClickJoin = null,
  showArchived = null,
  loading,
  filter = null,
}) => {
  const currentId = useSelector(currentChannelId);
  const authEmployee = useSelector(authEmployeeSelector);
  const [visibleChannels, setVisibleChannels] = useState([]);
  const [archivedChannels, setArchivedChannels] = useState([]);
  const dispatch = useDispatch();
  const { channelId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const currentMessageId = useMemo(() => state?.messageId || null, [state]);

  useEffect(() => {
    const firstId = firstVisibleChannelId(visibleChannels);
    const foundChannel =
      visibleChannels.find(
        c => !c.separator && c._id != null && channelIdsEqual(c._id, channelId),
      ) || state?.channel;
    const nextId = resolveMessagesChannelRedirect({
      loading,
      channelId,
      firstId,
      channelInList: Boolean(foundChannel),
    });
    if (nextId) {
      navigate(`/home/messages/${nextId}`);
      return;
    }
    if (
      foundChannel &&
      (!channelIdsEqual(channelId, currentId) || messageId !== currentMessageId)
    ) {
      dispatch(actions.channels.open(channelId, currentMessageId));
    }
  }, [
    channelId,
    currentId,
    currentMessageId,
    dispatch,
    loading,
    messageId,
    navigate,
    state?.channel,
    state?.messageId,
    visibleChannels,
  ]);

  const filterChannels = useCallback(() => {
    const newVisibleChannels = channels.filter(
      c => filter || !showArchived === !authEmployee?.archivedChannels?.includes(c._id),
    );

    const newArchivedChannels = channels.filter(c =>
      authEmployee?.archivedChannels?.includes(c._id),
    );
    setArchivedChannels(newArchivedChannels);
    setVisibleChannels(newVisibleChannels);
  }, [channels, authEmployee, showArchived, filter]);

  useEffect(() => {
    filterChannels();
  }, [authEmployee?.archivedChannels, channels, currentId, filterChannels, showArchived]);

  const getItemSize = useCallback(
    index => {
      return visibleChannels[index]?.separator ? 36 : 80;
    },
    [visibleChannels],
  );

  const rowProps = useMemo(
    () => ({
      visibleChannels,
      currentId,
      messageId,
      onSelect,
      onClickJoin,
    }),
    [currentId, messageId, onClickJoin, onSelect, visibleChannels],
  );

  if (!visibleChannels) {
    return null;
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& div': {
          outline: 'none',
        },
      }}
    >
      {archivedChannels?.length > 0 && <ArchivedChannelsButton />}
      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <List
          rowComponent={Row}
          rowCount={visibleChannels.length}
          rowHeight={index => getItemSize(index)}
          rowProps={rowProps}
          style={{ flex: '1 1 auto', height: '100%', width: '100%', minHeight: 0 }}
        />
      </div>
    </Box>
  );
};

Channels.propTypes = {
  channels: array.isRequired,
  messageId: string,
  onSelect: func.isRequired,
  onClickJoin: func,
  showArchived: bool,
  loading: bool.isRequired,
  filter: string,
};

export default memo(
  Channels,
  (prevProps, nextProps) =>
    prevProps.loading === nextProps.loading &&
    prevProps.filter === nextProps.filter &&
    prevProps.messageId === nextProps.messageId &&
    prevProps.showArchived === nextProps.showArchived &&
    prevProps.channels.length === nextProps.channels.length &&
    prevProps.channels.every((c, i) => compareChannel(c, nextProps.channels[i])) &&
    prevProps.onSelect === nextProps.onSelect,
);
